import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";
import api from "@/lib/api";
import { priceParts } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { useSaves } from "@/context/SavesContext";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";

// The Saved page: every listing the renter bookmarked, as thumbnails that
// link to the listing detail. Reads the SavesContext (already hydrated from
// GET /api/saves on sign-in) as the source of truth, and refetches once on
// mount so a fresh visit always reflects the server. Honest empty state.
export default function SavedPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items } = useSaves();
  const [serverItems, setServerItems] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/saves")
      .then((res) => {
        if (!alive) return;
        setServerItems(Array.isArray(res.data?.saves) ? res.data.saves : []);
      })
      .catch(() => alive && setServerItems(null));
    return () => { alive = false; };
  }, [user?.id]);

  // Prefer the live context (optimistic toggles show instantly); fall back to
  // the server snapshot for the first paint.
  const saved = items.length ? items : serverItems || [];
  const loading = serverItems === null && items.length === 0;

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-4xl px-4 pt-8 sm:px-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-night-text">
          <Bookmark size={22} className="fill-brand-primary text-brand-primary" /> Saved homes
        </h1>
        <p className="mt-1 text-sm text-night-sub">
          The homes you bookmarked. Tap any one to see the full listing.
        </p>

        {loading ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : saved.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <Bookmark className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No saved homes yet</h2>
            <p className="mt-1 text-sm text-night-sub">
              Tap the bookmark on a home while you swipe to keep it here.
            </p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/swipe")}>Start swiping</ButtonGhost>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {saved.map((l) => (
              <Link key={l.id} to={`/listing/${l.id}`} className="group">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={l.photos?.[0]}
                    alt={l.title}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]"
                  />
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-night-text">{l.title}</p>
                <p className="text-xs text-night-sub">
                  ${priceParts(l).amount.toLocaleString()} {priceParts(l).unit}
                </p>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
